import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useAuthContext } from 'AuthProvider'
import { AgGridReact } from 'ag-grid-react'
import { EditStatusCellRenderer, LoadingOverlay } from 'components/commonparts/index'
import { useCenter } from 'hooks/useCenter'
import { useGridEdit } from 'hooks/useGridEdit'
import { initRecordEditEx, type DataListPropsBase, type RecordEditEx } from 'types/dataGridTypes'
import { validateRequired } from 'utils/validation'

import type { ColDef, ColGroupDef } from 'ag-grid-enterprise'
import type { Center, MstCenter, GetMstCentersCenterGetRequest } from 'api-client'
import 'utils/mst.scss'

const columnMapping: Record<string, string> = {
  org_center_id: 'orgCenterId',
  center_name: 'centerName',
  center_address: 'centerAddress',
  ship_previous_stock_flg: 'shipPreviousStockFlg',
  today_ship_rec_delay_flg: 'todayShipRecDelayFlg',
  calc_stock_shippingorder_flg: 'calcStockShippingorderFlg',
  internal_transfer_hub_flg: 'internalTransferHubFlg',
  internal_inbound_flg: 'internalInboundFlg',
  enable_shipday_mon: 'enableShipdayMon',
  enable_shipday_tue: 'enableShipdayTue',
  enable_shipday_wed: 'enableShipdayWed',
  enable_shipday_thu: 'enableShipdayThu',
  enable_shipday_fri: 'enableShipdayFri',
  enable_shipday_sat: 'enableShipdaySat',
  enable_shipday_sun: 'enableShipdaySun',
  enable_arrival_mon: 'enableArrivalMon',
  enable_arrival_tue: 'enableArrivalTue',
  enable_arrival_wed: 'enableArrivalWed',
  enable_arrival_thu: 'enableArrivalThu',
  enable_arrival_fri: 'enableArrivalFri',
  enable_arrival_sat: 'enableArrivalSat',
  enable_arrival_sun: 'enableArrivalSun',
  enable_orderday_mon: 'enableOrderdayMon',
  enable_orderday_tue: 'enableOrderdayTue',
  enable_orderday_wed: 'enableOrderdayWed',
  enable_orderday_thu: 'enableOrderdayThu',
  enable_orderday_fri: 'enableOrderdayFri',
  enable_orderday_sat: 'enableOrderdaySat',
  enable_orderday_sun: 'enableOrderdaySun',
  center_group_name: 'centerGroupName',
  center_gln: 'centerGln',
  remarks: 'remarks',
  delete_flg: 'deleteFlg',
}
const primayKey = ['orgCenterId']

interface DataListProps extends DataListPropsBase {
  list: Center
  searchParam: GetMstCentersCenterGetRequest
  gridRef?: React.RefObject<AgGridReact>
  findSearchValue?: string
  onFindChanged?: (event: any) => void
  isSearchVisible?: boolean
}

interface MstCenterWithId extends MstCenter, RecordEditEx {}

function DataList({
  list,
  isLoading,
  searchParam,
  onReload,
  updateTrigger,
  onUploadData,
  setHasGridEdited,
  setResultMessage,
  setDownloadColumnDefs,
  gridRef: externalGridRef,
  findSearchValue: externalFindSearchValue,
  onFindChanged: externalOnFindChanged,
  isSearchVisible = false,
}: DataListProps) {
  const { loginUserInfo } = useAuthContext()
  const { fetchCenterUpdate, isUpdating, result } = useCenter()
  const internalGridRef = useRef<AgGridReact<MstCenterWithId>>(null)
  const gridRef = externalGridRef || internalGridRef
  
  const containerStyle = useMemo(() => {
    const searchBoxHeight = isSearchVisible ? 40 : 0
    return { width: '100%', height: `calc(100vh - ${120 + searchBoxHeight}px)` }
  }, [isSearchVisible])
  
  const gridStyle = useMemo(() => {
    const searchBoxHeight = isSearchVisible ? 40 : 0
    return { width: '100%', height: `calc(100vh - ${120 + searchBoxHeight}px)` }
  }, [isSearchVisible])
  const [row, setRow] = useState<MstCenterWithId[]>(list.list.map((item) => ({ ...item, ...initRecordEditEx() })))

  useEffect(() => {
    setResultMessage(result)
  }, [result])

  const createBlankRow = useCallback((): MstCenterWithId => {
    const blankRow: Partial<MstCenterWithId> = {
      companyId: loginUserInfo?.companyId || null,
      orgCenterId: null,
      centerId: null,
      centerName: null,
      centerAddress: null,
      shipPreviousStockFlg: false,
      todayShipRecDelayFlg: false,
      calcStockShippingorderFlg: false,
      enableShipdayMon: true,
      enableShipdayTue: true,
      enableShipdayWed: true,
      enableShipdayThu: true,
      enableShipdayFri: true,
      enableShipdaySat: true,
      enableShipdaySun: false,
      enableArrivalMon: true,
      enableArrivalTue: true,
      enableArrivalWed: true,
      enableArrivalThu: true,
      enableArrivalFri: true,
      enableArrivalSat: true,
      enableArrivalSun: false,
      enableOrderdayMon: true,
      enableOrderdayTue: true,
      enableOrderdayWed: true,
      enableOrderdayThu: true,
      enableOrderdayFri: true,
      enableOrderdaySat: false,
      enableOrderdaySun: false,
      internalTransferHubFlg: false,
      internalInboundFlg: false,
      centerGroupName: null,
      centerGln: null,
      remarks: null,
      deleteFlg: false,
      insertDatetime: null,
      updateDatetime: null,
      insertUser: loginUserInfo?.userId || null,
      deleteDatetime: null,
      deleteUser: null,
      updateUser: null,
      ...initRecordEditEx(true),
    }
    return blankRow as MstCenterWithId
  }, [])

  // 入力チェック
  const fieldValidate = (pRow: MstCenterWithId, field: string | undefined, newValue: any): boolean => {
    let hasErrors = false
    switch (field) {
      case 'orgCenterId': {
        hasErrors = checkAndSetError(pRow, field, validateRequired(newValue))
        if (!hasErrors) {
          hasErrors = duplicateCheck(pRow, newValue)
        }
        break
      }
      case 'centerName': {
        hasErrors = checkAndSetError(pRow, field, validateRequired(newValue))
        break
      }
      default:
        return false
    }
    // console.log('fieldValidate', field, pRow, hasErrors)
    return hasErrors
  }

  const updateMstCenter = useCallback(
    async (updatedRows: MstCenterWithId[]) => {
      const datalist = updatedRows.map(({ isEdited, orgCenterId, centerName, editedFields, errors, ...rest }) => ({
        ...rest,
        orgCenterId: orgCenterId ?? '',
        centerName: centerName ?? '',
      }))

      try {
        await fetchCenterUpdate({
          centerUpdate: { body: datalist },
        })
        if (onReload) onReload()
      } catch (err: any) {
        if (err?.errors && err.result?.status === 'error') {
          const gridApi = gridRef.current?.api

          err.errors.forEach((errrow: any) => {
            const { rowdata } = errrow
            const orgCenterId = (rowdata as { org_center_id: string | null })?.org_center_id
            const errRow = updatedRows.find((rowdatas) => rowdatas.orgCenterId === orgCenterId)
            if (errRow) {
              // UpdateAPIのErrorは、PKをエラー表記して、サーバーから取得したエラーを表示する
              errRow.errors = { orgCenterId: [errrow.errorMessage] }
              const rowNode = gridApi?.getRowNode(errRow.id || '')
              if (rowNode) {
                rowNode.setData(errRow)
              }
            }
          })
        }
      }
    },
    [fetchCenterUpdate, searchParam]
  )

  const handleImportExcel = (data: any[]) => {
    let hasError = false
    const mappedData = data.map((excelRow: any) => {
      const mappedRow: Partial<MstCenterWithId> = {}
      Object.keys(columnMapping).forEach((key) => {
        if (key in excelRow) {
          const tableField = columnMapping[key as keyof typeof columnMapping]
          const importvalue = excelRow[key]
          let setvalue = importvalue || null

          if (
            [
              'deleteFlg',
              'shipPreviousStockFlg',
              'todayShipRecDelayFlg',
              'calcStockShippingorderFlg',
              'internalTransferHubFlg',
              'internalInboundFlg',
              'enableShipdayMon',
              'enableShipdayTue',
              'enableShipdayWed',
              'enableShipdayThu',
              'enableShipdayFri',
              'enableShipdaySat',
              'enableShipdaySun',
              'enableArrivalMon',
              'enableArrivalTue',
              'enableArrivalWed',
              'enableArrivalThu',
              'enableArrivalFri',
              'enableArrivalSat',
              'enableArrivalSun',
              'enableOrderdayMon',
              'enableOrderdayTue',
              'enableOrderdayWed',
              'enableOrderdayThu',
              'enableOrderdayFri',
              'enableOrderdaySat',
              'enableOrderdaySun',
            ].includes(tableField)
          ) {
            setvalue = parseBoolean(importvalue)
          } else {
            setvalue = importvalue ? String(importvalue).trim() : null
          }
          // パース出来ないときはスキップ（＝NULL）し、警告メッセージを表示
          if (!isNonInputValue(importvalue) && setvalue === null) hasError = true
          mappedRow[tableField as keyof MstCenterWithId] = setvalue as unknown as undefined
        }
      })
      return mappedRow as MstCenterWithId
    })
    return { mappedData, hasError }
  }

  const {
    booleanFormatter,
    dateFormatter,
    excelStyles,
    selectValueFormatter,
    typecodeFormatter,
    formatBooleanValue,
    formatDecimalValue,
    isNonInputValue,
    numberValueFormatter,
    numberValueParser,
    parseBoolean,
    parseBooleanValue,
    parseDate,
    parseDateValue,
    parseDecimalValue,
    parseNumeric,
    parsePositiveInteger,
    parseStringValue,
    parseTime,
    selectTypecodeValueParser,
    selectValueParser,
    typeCodeParser,
    localeText,
    autoSizeStrategy,
    onGridReady,
    getCellClassRules,
    checkAndSetError,
    rowDataValidate,
    colDataValidate,
    duplicateCheck,
    isEnableEdit,
    longTextEditorParams,
    longTextValueFormatter,
    longTextValueParser,
    longTextValueSetter,
    editInfoValueGetter,
    makeWeekDayChiled,
    clearPinnedRows,
  } = useGridEdit({
    gridRef,
    isLoading,
    isUpdating,
    onReload,
    onUploadData,
    updateTrigger,
    setHasGridEdited,
    setResultMessage,
    columnMapping,
    primayKey,
    storageKey: 'gridColumnState:/master-center',
    updateData: updateMstCenter,
    handleImportExcel,
    createBlankRow,
    fieldValidate,
    scrollPositionKey: 'orgCenterId',
    rowData: row,
  })

  /**
   * カラム定義
   * @returns
   */
  const updateColumnDefs = (): (ColDef | ColGroupDef)[] => [
    {
      headerName: '!',
      rowDrag: true,
      cellRenderer: EditStatusCellRenderer,
      valueGetter: (params) => editInfoValueGetter(params),
      pinned: 'left',
      width: 100,
      filter: 'agMultiColumnFilter',
      filterParams: {
        filters: [
          {
            filter: 'agSetColumnFilter',
            filterParams: {
              values: ['入力エラー', '変更', null],
              refreshValuesOnOpen: true,
              applyMiniFilterWhileTyping: true,
              excelMode: 'mac',
            },
          },
        ],
        buttons: ['reset'],
        closeOnApply: true,
        applyMiniFilterWhileTyping: true,
        excelMode: 'mac',
      },
    },
    {
      headerName: '基本情報',
      children: [
        {
          headerName: '自社センターID',
          field: 'orgCenterId',
          editable: (params) => isEnableEdit(params.data && params.data.isNewRecord),
          cellClassRules: {
            ...getCellClassRules(),
            'editable-cell': (params) => isEnableEdit(params.data && params.data.insertDatetime === null),
          },
          pinned: 'left',
          width: 200,
        },
        {
          headerName: 'PSIセンターID',
          field: 'centerId',
          width: 200,
        },
        {
          headerName: 'センター名',
          field: 'centerName',
          width: 350,
          editable: isEnableEdit(),
          cellClassRules: getCellClassRules(),
          pinned: 'left',
        },
        {
          headerName: 'センター所在地住所',
          field: 'centerAddress',
          editable: isEnableEdit(),
          cellClassRules: getCellClassRules(),
          width: 200,
        },
      ],
    },
    {
      headerName: '発注設定',
      children: [
        {
          headerName: '前日在庫引当',
          field: 'shipPreviousStockFlg',
          width: 200,
          editable: isEnableEdit(),
          cellRenderer: 'agCheckboxCellRenderer',
          cellEditor: 'agCheckboxCellEditor',
          valueFormatter: formatBooleanValue,
          valueParser: parseBooleanValue,
        },
        {
          headerName: '本日出荷数に予測値を使用',
          field: 'todayShipRecDelayFlg',
          width: 200,
          editable: isEnableEdit(),
          cellRenderer: 'agCheckboxCellRenderer',
          cellEditor: 'agCheckboxCellEditor',
          valueFormatter: formatBooleanValue,
          valueParser: parseBooleanValue,
        },
        {
          headerName: '在庫数に受注済出荷予定を含める',
          field: 'calcStockShippingorderFlg',
          editable: isEnableEdit(),
          cellRenderer: 'agCheckboxCellRenderer',
          cellEditor: 'agCheckboxCellEditor',
          valueFormatter: formatBooleanValue,
          valueParser: parseBooleanValue,
        },
      ],
    },
    {
      headerName: '出荷可能曜日',
      children: makeWeekDayChiled('enableShipday', {
        editable: isEnableEdit(),
        width: 70,
        cellRenderer: 'agCheckboxCellRenderer',
        cellEditor: 'agCheckboxCellEditor',
        valueFormatter: formatBooleanValue,
        valueParser: parseBooleanValue,
      }),
    },
    {
      headerName: '入荷可能曜日',
      children: makeWeekDayChiled('enableArrival', {
        editable: isEnableEdit(),
        width: 70,
        cellRenderer: 'agCheckboxCellRenderer',
        cellEditor: 'agCheckboxCellEditor',
        valueFormatter: formatBooleanValue,
        valueParser: parseBooleanValue,
      }),
    },
    {
      headerName: '発注業務可能曜日',
      children: [
        ...makeWeekDayChiled('enableOrderday', {
          editable: isEnableEdit(),
          width: 70,
          cellRenderer: 'agCheckboxCellRenderer',
          cellEditor: 'agCheckboxCellEditor',
          valueFormatter: formatBooleanValue,
          valueParser: parseBooleanValue,
        }),
      ],
    },
    {
      headerName: '移送設定',
      children: [
        {
          headerName: '出荷先に自社センターを含む',
          field: 'internalTransferHubFlg',
          width: 200,
          editable: isEnableEdit(),
          cellRenderer: 'agCheckboxCellRenderer',
          cellEditor: 'agCheckboxCellEditor',
          valueFormatter: formatBooleanValue,
          valueParser: parseBooleanValue,
        },
        {
          headerName: '発注先に自社センターを含む',
          field: 'internalInboundFlg',
          width: 200,
          editable: isEnableEdit(),
          cellRenderer: 'agCheckboxCellRenderer',
          cellEditor: 'agCheckboxCellEditor',
          valueFormatter: formatBooleanValue,
          valueParser: parseBooleanValue,
        },
      ],
    },
    {
      headerName: 'センターグループ名',
      field: 'centerGroupName',
      editable: isEnableEdit(),
      width: 200,
    },
    {
      headerName: 'GS1事業者コード',
      field: 'centerGln',
      editable: isEnableEdit(),
      width: 200,
    },
    {
      headerName: '備考',
      field: 'remarks', 
      editable: isEnableEdit(),
      width: 200,
      cellEditorPopup: true,
      cellEditor: 'agLargeTextCellEditor',
      cellEditorParams: longTextEditorParams,
      valueFormatter: longTextValueFormatter,
      valueParser: longTextValueParser,
      valueSetter: longTextValueSetter,
    },
    {
      headerName: '削除',
      width: 200,
      field: 'deleteFlg', // 削除
      editable: isEnableEdit(),
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      valueFormatter: formatBooleanValue,
      valueParser: parseBooleanValue,
    },
  ]

  const columnDefs = useMemo<ColDef[]>(updateColumnDefs, [])
  // ダウンロードファイル用のカラム定義
  useEffect(() => {
    if (setDownloadColumnDefs)
      setDownloadColumnDefs([
        {
          headerName: '自社センターID',
          children: [{ headerName: 'org_center_id', field: 'orgCenterId', cellClass: 'stringType' }],
        },
        { headerName: 'PSI企業ID', children: [{ headerName: 'company_id', field: 'companyId' }] },
        {
          headerName: 'PSIセンターID',
          children: [{ headerName: 'center_id', field: 'centerId', cellClass: 'stringType' }],
        },
        {
          headerName: 'センター名',
          children: [{ headerName: 'center_name', field: 'centerName', cellClass: 'stringType' }],
        },
        {
          headerName: 'センター所在地住所',
          children: [{ headerName: 'center_address', field: 'centerAddress', cellClass: 'stringType' }],
        },
        {
          headerName: '前日在庫引当',
          children: [
            { headerName: 'ship_previous_stock_flg', field: 'shipPreviousStockFlg', cellClass: 'booleanType' },
          ],
        },
        {
          headerName: '本日出荷数に予測値を使用',
          children: [
            { headerName: 'today_ship_rec_delay_flg', field: 'todayShipRecDelayFlg', cellClass: 'booleanType' },
          ],
        },
        {
          headerName: '在庫数に受注済出荷予定を含める',
          children: [
            { headerName: 'calc_stock_shippingorder_flg', field: 'calcStockShippingorderFlg', cellClass: 'booleanType' },
          ],
        },
        {
          headerName: '出荷可能曜日（月曜日）',
          children: [{ headerName: 'enable_shipday_mon', field: 'enableShipdayMon', cellClass: 'booleanType' }],
        },
        {
          headerName: '出荷可能曜日（火曜日）',
          children: [{ headerName: 'enable_shipday_tue', field: 'enableShipdayTue', cellClass: 'booleanType' }],
        },
        {
          headerName: '出荷可能曜日（水曜日）',
          children: [{ headerName: 'enable_shipday_wed', field: 'enableShipdayWed', cellClass: 'booleanType' }],
        },
        {
          headerName: '出荷可能曜日（木曜日）',
          children: [{ headerName: 'enable_shipday_thu', field: 'enableShipdayThu', cellClass: 'booleanType' }],
        },
        {
          headerName: '出荷可能曜日（金曜日）',
          children: [{ headerName: 'enable_shipday_fri', field: 'enableShipdayFri', cellClass: 'booleanType' }],
        },
        {
          headerName: '出荷可能曜日（土曜日）',
          children: [{ headerName: 'enable_shipday_sat', field: 'enableShipdaySat', cellClass: 'booleanType' }],
        },
        {
          headerName: '出荷可能曜日（日曜日）',
          children: [{ headerName: 'enable_shipday_sun', field: 'enableShipdaySun', cellClass: 'booleanType' }],
        },
        {
          headerName: '入荷可能曜日（月曜日）',
          children: [{ headerName: 'enable_arrival_mon', field: 'enableArrivalMon', cellClass: 'booleanType' }],
        },
        {
          headerName: '入荷可能曜日（火曜日）',
          children: [{ headerName: 'enable_arrival_tue', field: 'enableArrivalTue', cellClass: 'booleanType' }],
        },
        {
          headerName: '入荷可能曜日（水曜日）',
          children: [{ headerName: 'enable_arrival_wed', field: 'enableArrivalWed', cellClass: 'booleanType' }],
        },
        {
          headerName: '入荷可能曜日（木曜日）',
          children: [{ headerName: 'enable_arrival_thu', field: 'enableArrivalThu', cellClass: 'booleanType' }],
        },
        {
          headerName: '入荷可能曜日（金曜日）',
          children: [{ headerName: 'enable_arrival_fri', field: 'enableArrivalFri', cellClass: 'booleanType' }],
        },
        {
          headerName: '入荷可能曜日（土曜日）',
          children: [{ headerName: 'enable_arrival_sat', field: 'enableArrivalSat', cellClass: 'booleanType' }],
        },
        {
          headerName: '入荷可能曜日（日曜日）',
          children: [{ headerName: 'enable_arrival_sun', field: 'enableArrivalSun', cellClass: 'booleanType' }],
        },
        {
          headerName: '発注業務可能曜日（月曜日）',
          children: [{ headerName: 'enable_orderday_mon', field: 'enableOrderdayMon', cellClass: 'booleanType' }],
        },
        {
          headerName: '発注業務可能曜日（火曜日）',
          children: [{ headerName: 'enable_orderday_tue', field: 'enableOrderdayTue', cellClass: 'booleanType' }],
        },
        {
          headerName: '発注業務可能曜日（水曜日）',
          children: [{ headerName: 'enable_orderday_wed', field: 'enableOrderdayWed', cellClass: 'booleanType' }],
        },
        {
          headerName: '発注業務可能曜日（木曜日）',
          children: [{ headerName: 'enable_orderday_thu', field: 'enableOrderdayThu', cellClass: 'booleanType' }],
        },
        {
          headerName: '発注業務可能曜日（金曜日）',
          children: [{ headerName: 'enable_orderday_fri', field: 'enableOrderdayFri', cellClass: 'booleanType' }],
        },
        {
          headerName: '発注業務可能曜日（土曜日）',
          children: [{ headerName: 'enable_orderday_sat', field: 'enableOrderdaySat', cellClass: 'booleanType' }],
        },
        {
          headerName: '発注業務可能曜日（日曜日）',
          children: [{ headerName: 'enable_orderday_sun', field: 'enableOrderdaySun', cellClass: 'booleanType' }],
        },
        {
          headerName: '出荷先に自社センターを含む',
          children: [{ headerName: 'internal_transfer_hub_flg', field: 'internalTransferHubFlg', cellClass: 'booleanType' }],
        },
        {
          headerName: '発注先に自社センターを含む',
          children: [{ headerName: 'internal_inbound_flg', field: 'internalInboundFlg', cellClass: 'booleanType' }],
        },
        {
          headerName: 'センターグループ名',
          children: [{ headerName: 'center_group_name', field: 'centerGroupName', cellClass: 'stringType' }],
        },
        {
          headerName: 'GS1事業者コード',
          children: [{ headerName: 'center_gln', field: 'centerGln', cellClass: 'stringType' }],
        },
        { headerName: '備考', children: [{ headerName: 'remarks', field: 'remarks', cellClass: 'stringType' }] },
        {
          headerName: '削除',
          children: [{ headerName: 'delete_flg', field: 'deleteFlg', cellClass: 'booleanType' }],
        },
      ])

  }, [setDownloadColumnDefs])

  useEffect(() => {
    if (list.list.length === 0) {
      const blankRow = createBlankRow()
      setRow([blankRow])
    } else {
      setRow(list.list.map((item) => ({ ...item, ...initRecordEditEx() })))
      clearPinnedRows()
    }
  }, [list.list])

  return (
    <div style={{ ...containerStyle, display: 'flex', flexDirection: 'column', height: '69vh' }}>
      <div style={{ flexGrow: 1, boxSizing: 'border-box' }}>
        <div style={gridStyle} className="ag-theme-balham">
          <AgGridReact
            rowData={row}
            ref={gridRef}
            getRowId={(params) => params.data.id}
            columnDefs={columnDefs}
            onGridReady={onGridReady}
            localeText={localeText}
            autoSizeStrategy={autoSizeStrategy}
            loadingOverlayComponent={LoadingOverlay}
            findSearchValue={externalFindSearchValue}
            onFindChanged={externalOnFindChanged}
            suppressColumnVirtualisation
            stopEditingWhenCellsLoseFocus
          />
        </div>
      </div>
    </div>
  )
}

export default DataList
